<script setup lang="ts">
import type { PropType } from "vue";
import StatusIcon from "../participants/StatusIcon.vue";
import MoveParticipant from "../participants/MoveParticipant.vue";
import ResetQuestionnaire from "../participants/ResetQuestionnaire.vue";
import RegenerateParticipant from "../participants/RegenerateParticipant.vue";
import { useI18n } from "vue-i18n";
import { Participant, Project } from "../../types/PersologOnlineTypes.ts";

defineProps({
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    participants: {
        type: Object as PropType<Participant[]>,
        required: true,
    },
});

// A moved participant leaves this project, so its participant list has to be fetched again.
const emit = defineEmits<{ reload: [] }>();

const { t } = useI18n();
</script>

<template>
    <div class="p-1" v-if="participants.length > 0 && participants.length !== undefined">
        <h5>{{ t("projects.heading", { project: project?.name }) }}</h5>
        <DataTable :value="participants">
            <Column field="id" :header="t('projects.id')" :sortable="true"></Column>
            <Column field="name" :header="t('projects.name')">
                <template #body="slotProps">
                    {{ slotProps.data.firstName.concat(" " + slotProps.data.name) }}
                </template>
            </Column>
            <Column field="email" :header="t('projects.email')" :sortable="true"></Column>
            <Column field="status" :header="t('projects.status')">
                <template #body="slotProps">
                    <StatusIcon :status="slotProps.data.status"></StatusIcon>
                </template>
            </Column>
            <Column :header="t('projects.actions')">
                <template #body="slotProps">
                    <div class="flex items-center">
                        <RegenerateParticipant :participant="slotProps.data" />
                        <MoveParticipant :participant="slotProps.data" :project="project" @moved="emit('reload')" />
                        <ResetQuestionnaire :participant="slotProps.data" @reset="emit('reload')" />
                    </div>
                </template>
            </Column>
        </DataTable>
    </div>
    <p v-else>{{ t("projects.empty", { project: project?.name }) }}</p>
</template>
